import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOutUser } from '../firebase/auth';
import BackToMarketing from './BackToMarketing';

const navItems = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
      </svg>
    ),
  },
  {
    name: 'Voice Agent',
    href: '/app',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    ),
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const LogoutIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOutUser();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (href) => location.pathname === href;

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex w-60 flex-col h-full"
        style={{ backgroundColor: '#0d0d18', borderRight: '0.5px solid rgba(255,255,255,0.06)' }}
      >
        {/* Logo */}
        <div className="px-5 py-5" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
          <h1 className="text-lg font-extrabold text-white tracking-tight">Appify.AI</h1>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>Voice AI Platform</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition"
              style={
                isActive(item.href)
                  ? { background: 'linear-gradient(135deg, rgba(139,92,246,0.25), rgba(192,132,252,0.15))', color: '#C084FC', border: '0.5px solid rgba(139,92,246,0.25)', boxShadow: '0 0 12px rgba(139,92,246,0.1)' }
                  : { color: 'rgba(255,255,255,0.5)' }
              }
            >
              <span style={isActive(item.href) ? { color: '#C084FC' } : {}}>{item.icon}</span>
              {item.name}
            </Link>
          ))}
        </nav>

        {/* Back to marketing */}
        <div className="px-3 pb-2" style={{ borderTop: '0.5px solid rgba(255,255,255,0.06)', paddingTop: '12px' }}>
          <BackToMarketing
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm w-full transition"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          />
        </div>

        {/* Logout */}
        <div className="px-3 pb-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm transition"
            style={{ color: 'rgba(239,68,68,0.7)' }}
          >
            <LogoutIcon />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 px-2 py-2"
        style={{ backgroundColor: 'rgba(13,13,24,0.97)', backdropFilter: 'blur(12px)', borderTop: '0.5px solid rgba(255,255,255,0.08)' }}
      >
        <div className="grid grid-cols-5 gap-1 max-w-xl mx-auto">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="flex flex-col items-center justify-center gap-1 rounded-xl py-2 text-[10px] font-medium transition"
              style={
                isActive(item.href)
                  ? { background: 'linear-gradient(135deg, rgba(139,92,246,0.3), rgba(192,132,252,0.15))', color: '#C084FC' }
                  : { color: 'rgba(255,255,255,0.45)' }
              }
            >
              <span>{item.icon}</span>
              <span>{item.name}</span>
            </Link>
          ))}
          <button
            onClick={handleLogout}
            className="flex flex-col items-center justify-center gap-1 rounded-xl py-2 text-[10px] font-medium transition"
            style={{ color: 'rgba(239,68,68,0.65)' }}
          >
            <LogoutIcon />
            <span>Logout</span>
          </button>
          <BackToMarketing
            className="flex flex-col items-center justify-center gap-1 rounded-xl py-2 text-[10px]"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          />
        </div>
      </nav>
    </>
  );
};

export default Sidebar;
